<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ErrorMessages from "../../global/ErrorMessages.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import CancellationDiscountModal from "./CancellationDiscountModal.vue";
import DiscountSuccessModal from "./DiscountSuccessModal.vue";
import {computed, ref} from "vue";
import Swal from 'sweetalert2'
import {useStore} from "vuex";
import axios from "axios";
import {errorToast} from "@/composables/useToasts";
import {useMutation} from "@tanstack/vue-query";

const store = useStore()
const cancellationReason = ref('')
const whatToImprove = ref('')
const showDiscountModal = ref(false)
const showSuccessModal = ref(false)
const cancellationReasons = computed(() => store.getters['agencyCancellation/cancellationReasons'])
const isPackage = computed(() => store.getters['agencyCancellation/isPackage'])
const isAnnual = computed(() => store.getters['agencyCancellation/isAnnual'])
const organisationId = computed(() => store.getters['agencyCancellation/organisationId'])
const hasDiscountApplied = computed(() => store.getters['agencyCancellation/hasDiscountApplied'])
const maxNumberOfDiscountsApplied = computed(() => store.getters['agencyCancellation/maxNumberOfDiscountsApplied'])
const errors = computed(() => store.getters['agencyCancellation/validationErrors'])

const handleCancelled = () => {
  if (cancellationReason.value === 'TECHNICAL_ISSUES') {
    Swal.fire({
      icon: 'warning',
      title: 'We\'re sorry to see you go!',
      text: 'We\'re very sorry you have experienced technical issues with Hike. We may be in '
        + 'touch to learn more so we can improve the platform for everyone moving forwards.',
      confirmButtonText: 'Continue',
      didClose: () => window.location = '/'
    })
    return
  }

  window.location = '/'
}

const cancellationFailedMessage = (error) => error?.response?.data?.message
  ?? 'Sorry, there was a problem cancelling your account. Please contact support.';

const showCancellationConfirmation = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Are you sure?',
    text: 'By cancelling your account you will lose all of your current SEO progress!',
    showCancelButton: true,
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, Cancel Account!',
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    preConfirm: async () => {
      try {
        await store.dispatch('agencyCancellation/submitCancellationForm', {
          cancellationReason: cancellationReason.value,
          whatToImprove: whatToImprove.value,
        });
      } catch (error) {
        if (error.response?.status === 422) {
          return false;
        }

        Swal.close();

        await Swal.fire({
          icon: 'error',
          title: 'Cancellation failed',
          text: cancellationFailedMessage(error),
        });

        return false;
      }
    },
  }).then((result) => {
    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      icon: 'success',
      title: 'Your plan has been successfully cancelled',
      text: !isPackage.value
        ? 'If you have no active websites your account will close immediately. Otherwise it will still be accessible until the end of your current billing period.'
        : 'Your account will still be accessible until the end of your current billing period.',
      didClose: () => handleCancelled(),
    });
  });
};

const shouldShowDiscountModal = computed(() => {
  if (isAnnual.value || hasDiscountApplied.value) {
    return false;
  }

  if (!isPackage.value) {
    return false;
  }

  return !maxNumberOfDiscountsApplied.value;
});

const submitForm = () => {
  if (shouldShowDiscountModal.value) {
    showDiscountModal.value = true;
    return;
  }

  showCancellationConfirmation();
}

const {mutate: handleAcceptDiscount, isPending: applyingDiscount} = useMutation({
  mutationFn: async (organisationId) => await axios.post( `/ajax/v2/settings/package/${organisationId}/discount`),
  onSuccess: () => {
    showDiscountModal.value = false;
    showSuccessModal.value = true;
  },
  onError: () => {
    errorToast('Failed to apply discount. Please try again.', 'Error');
  }
});

const handleSuccessModalClose = () => {
  showSuccessModal.value = false;
  window.location.href = '/dash';
};

const handleDeclineDiscount = () => {
  showDiscountModal.value = false;
  showCancellationConfirmation();
}
</script>

<template>
  <div class="tailwind">
    <CancellationDiscountModal
      v-if="showDiscountModal"
      :applying-discount="applyingDiscount"
      @close="handleDeclineDiscount"
      @confirm="handleAcceptDiscount(organisationId)"
    />

    <DiscountSuccessModal
      v-if="showSuccessModal"
      @close="handleSuccessModalClose"
    />

    <div class="tw-m-auto tw-max-w-default-container tw-rounded-xl tw-bg-white tw-p-8 tw-text-base">
      <div class="tw-flex tw-flex-col">
        <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-5 tw-leading-7">
          <p class="tw-text-base tw-text-neutral-900">
            We wanted to personally say thanks for trying our platform. We really wanted you to stick around for the longterm, but we understand you had to cancel your account 😢
          </p>
          <p class="tw-text-base tw-text-neutral-900">
            We have just two quick questions we hope you could answer that would help us improve the platform in the near future…
          </p>
        </div>

        <div>
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-2 tw-mb-4">
            <label
              for="cancellationReason"
              class="tw-text-base"
            >
              What's your reason for cancelling?
            </label>
            <CustomMultiselect
              id="cancellationReason"
              v-model="cancellationReason"
              class="tw-text-left tw-w-1/3"
              :close-on-select="true"
              :options="cancellationReasons"
              placeholder="Select a cancellation reason"
              :validation-errors="errors.cancellation_reason"
            />
          </div>

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
            <label
              for="cancellationReason"
              class="tw-text-base"
            >
              What can we improve?
            </label>
            <textarea
              v-model="whatToImprove"
              class="tw-rounded-lg tw-text-base tw-px-6 tw-py-3 tw-placeholder-neutral-400 tw-border tw-border-neutral"
              type="text"
              placeholder="Please let us know anything we can improve on, no matter how small."
              rows="5"
            />

            <ErrorMessages
              v-if="errors?.website"
              class="tw-self-baseline tw-absolute tw-top-12 tw-left-2"
              :errors="errors.website"
            />
          </div>
        </div>

        <div class="tw-mt-12 tw-flex tw-justify-between">
          <DefaultButton
            class="!tw-w-[189px]"
            @click="store.dispatch('agencyCancellation/previousStep')"
          >
            Go Back
          </DefaultButton>
          <div class="tw-flex tw-gap-3">
            <DefaultButton
              variant="danger"
              @click="submitForm"
            >
              Cancel Account
            </DefaultButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
